import { deleteKintoneSetting, deleteLinkServiceToken, type FeatureState, isKintoneTenantSetting, registerKintoneSetting } from './api';
import { initiateOAuth } from './utils';

export type settingContent = {
    id: string;
    name: string;
    state?: FeatureState;
} & MessageContent & ErrorMessage & ActionFunction  // 他の部分はメッセージとアクションを継承

//連携サービスコンポーネントの一つ一つの項目を定義する型。
// メッセージに関連するプロパティを別の型に分ける
type MessageContent = {
    enablingMessage?: string | React.ReactNode;
    enabledMessage?: string;
    disablingMessage?: string;
    disabledMessage?: string;
};

//エラー時のメッセージの文言
type ErrorMessage = {
    failedToEnabledMessage?: string;
    failedToDisabledMessage?: string;
};

// 動作を制御する関数を別の型に分ける
type ActionFunction = {
    submitFunction?: (data: Record<string, string>) => Promise<void>;
    isSettingEnabledFunction?: () => Promise<boolean>;  // 項目のオンオフを判定する関数
    turnOnFunction?: () => Promise<void>;  // 項目をオンにした際に呼び出す関数
    turnOffFunction?: () => Promise<void>; // 項目をオフにした際に呼び出す関数
};

export const userSettingId = {
    kintone: 'kintone'
} as const;
export type UserSettingId = typeof userSettingId[keyof typeof userSettingId];

export const tenantSettingId = {
    kintone: 'kintoneTenant'
} as const;
export type TenantSettingId = typeof tenantSettingId[keyof typeof tenantSettingId];

export const ServiceName = {
    kintone: 'kintone'
} as const;
export type ServiceName = typeof ServiceName[keyof typeof ServiceName];

export function getUserSettingContentId(id: string): settingContent | undefined {
    return userSettingContent.find(setting => setting.id === id);
}

export function getTenantSettingContentById(id: string): settingContent | undefined {
    return tenantSettingContent.find(setting => setting.id === id);
}

const tenantSettingContent: settingContent[] = [
    {
        id: tenantSettingId.kintone,
        name: 'kintoneテナントを環境に設定する(管理者設定)',
        //UserFormダイアログコンポーネントを使用するため、JSXの形式で指定する
        enablingMessage: (
            <>
                <h1>kintone管理者設定</h1>
                <p>以下の手順に従って、クライアントID、クライアントシークレット、トークンエンドポイントを設定してください。</p>
                <ol>
                    <li>
                        <a href='https://cybozu.dev/ja/common/docs/oauth-client/add-client/' target='_blank' rel='noopener noreferrer'>
                            こちらのサイト
                        </a>
                        のSTEP1、STEP2の設定を行ってください。パラメータは以下を入力してください。
                        <ul>
                            <li>クライアント名を<strong>Ricoh e-Whiteboard4200</strong>に設定</li>
                            <li>リダイレクトエンドポイントを<strong>{import.meta.env.VITE_DOMAIN}</strong>に設定</li>
                        </ul>
                        <p style={{ color: 'red' }}><strong>※STEP2の設定は、利用ユーザが増えるたびに再度実行する必要があります。</strong></p>
                    </li>
                    <li>表示されたクライアントID、クライアントシークレット、リダイレクトエンドポイントを下記に転記してください。</li>
                </ol>
            </>

        ),
        enabledMessage: 'kintoneのアカウント設定を登録しました。',
        disablingMessage: 'お客様のテナントのkintone設定を無効にします。テナント内のユーザー全員がkintone機能を使用できなくなります。',
        disabledMessage: 'お客様のkintoneアカウントの設定を消去しました。',
        submitFunction: async (data: Record<string, string>) => { registerKintoneSetting(data.clientId, data.secretId, data.endpointUrl); },
        isSettingEnabledFunction: () => { return isKintoneTenantSetting() },
        turnOffFunction: async () => { await deleteKintoneSetting() },
    }
]

const userSettingContent: settingContent[] = [
    {
        id: userSettingId.kintone,
        name: 'kintoneアカウントと連携する',
        enablingMessage: 'kintoneのログイン画面に遷移します。お客様のアカウントでログインをしてください。',
        enabledMessage: 'kintoneのアカウント設定を登録しました。',
        disablingMessage: 'お客様のkintoneアカウントの設定を消去します。',
        failedToEnabledMessage: 'kinotone連携が無効もしくは有効な設定値が入力されていません。管理者に設定項目「kintoneテナントを環境に設定する(管理者設定)」の実施を依頼してください',
        disabledMessage: 'お客様のkintoneアカウントの設定を消去しました。',
        turnOffFunction: async () => { await deleteLinkServiceToken(ServiceName.kintone) },
        turnOnFunction: async () => { await initiateOAuth(ServiceName.kintone) }
    }
]